import { useAuth } from '../../auth/auth-provider';

/**
 * Impersonation banner ("view as partner", ADR-0029).
 *
 * Sticky, full-width, unmissable strip shown while an admin/decision_maker
 * is browsing as a partner. Rendered at the app root (above the router) so it
 * spans both the admin shell and the portal shell. Being `sticky` (not
 * `fixed`) it occupies layout space, so the content below is pushed down
 * rather than overlapped.
 *
 * Also surfaces the transient "ended by reload" notice — the same component
 * owns both so there's a single place that talks about impersonation state.
 */
export function ImpersonationBanner() {
  const auth = useAuth();

  if (auth.impersonation) {
    const { target_email, target_company_name, actor_email } = auth.impersonation;
    return (
      <div
        role="status"
        className="sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-warning-300 bg-warning-100 px-4 py-2 text-center text-sm text-warning-800 dark:border-warning-500/40 dark:bg-warning-500/15 dark:text-warning-300"
      >
        <span>
          Prohlížíte jako <strong>{target_email}</strong>
          {target_company_name ? (
            <>
              {' '}
              (partner <strong>{target_company_name}</strong>)
            </>
          ) : null}
          {actor_email ? (
            <span className="text-warning-700/80 dark:text-warning-300/70">
              {' · '}přihlášen jako {actor_email}
            </span>
          ) : null}
        </span>
        <button
          type="button"
          onClick={() => void auth.endImpersonation()}
          className="rounded-md border border-warning-400 bg-white/70 px-3 py-1 text-xs font-semibold text-warning-800 transition-colors hover:bg-white dark:border-warning-500/50 dark:bg-transparent dark:text-warning-200 dark:hover:bg-white/10"
        >
          Ukončit zobrazení
        </button>
      </div>
    );
  }

  if (auth.impersonationNotice) {
    return (
      <div
        role="status"
        className="sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-gray-200 bg-gray-100 px-4 py-2 text-center text-sm text-gray-600 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-300"
      >
        <span>{auth.impersonationNotice}</span>
        <button
          type="button"
          onClick={auth.clearImpersonationNotice}
          className="rounded-md px-2 py-1 text-xs font-medium text-gray-500 underline-offset-2 hover:underline dark:text-gray-400"
        >
          Zavřít
        </button>
      </div>
    );
  }

  return null;
}
